import { db } from './Database';

export interface StackUnitLoadHeight {
    id?: number;
    palletLpnNumberId: number;
    variant: string;
    weekType: string;
    heightAB: string;
    heightBC: string;
    heightCD: string;
    heightDA: string;
    createdAt?: string;
    updatedAt?: string;
}

export class StackUnitLoadHeightService {
    // Get stack unit load height details for a pallet, variant and weekType
    static async getStackUnitLoadHeightDetails(palletId: number, variant: string, weekType: string): Promise<{ palletLpnNumbers: any[] }> {
        try {
            // First get all LPN numbers for the pallet
            const palletLpnNumbers = await db.palletLpnNumbers
                .where('palletId')
                .equals(palletId)
                .toArray();

            if (!palletLpnNumbers || palletLpnNumbers.length === 0) {
                throw new Error('Pallet LPN Numbers not found');
            }

            // Get unit load heights for each LPN number with specific week
            const response = await Promise.all(palletLpnNumbers.map(async palletLpnNumber => {
                const heights = await db.unitLoadHeights
                    .where('palletLpnNumberId')
                    .equals(palletLpnNumber.id!)
                    .filter(height => height.variant === variant && height.weekType === weekType)
                    .toArray();

                return {
                    ...palletLpnNumber,
                    unitLoadHeights: heights || []
                };
            }));

            return {
                palletLpnNumbers: response
            };
        } catch (error) {
            console.error('Error getting stack unit load height details:', error);
            throw error;
        }
    }

    // Get unique weeks for stack unit load height
    static async getStackUnitLoadHeightUniqueWeeks(palletId: number, variant: string): Promise<{ weeks: string[] }> {
        try {
            // Get all LPN numbers for the pallet
            const palletLpnNumbers = await db.palletLpnNumbers
                .where('palletId')
                .equals(palletId)
                .toArray();

            if (!palletLpnNumbers || palletLpnNumbers.length === 0) {
                return { weeks: [] };
            }

            // Get all unit load heights for these LPN numbers
            const uniqueWeeks = new Set<string>();

            for (const lpn of palletLpnNumbers) {
                const heights = await db.unitLoadHeights
                    .where('palletLpnNumberId')
                    .equals(lpn.id!)
                    .filter(height => height.variant === variant && height.weekType !== null)
                    .toArray();

                heights.forEach(height => {
                    if (height.weekType) uniqueWeeks.add(height.weekType);
                });
            }

            // Sort weeks in correct order
            const weekOrder = [
                "Stack start(stacked+30m)",
                "Stack Week-1",
                "Stack Week-2",
                "Stack Week-3",
                "Stack Week-4 (unstacking+30M)"
            ];

            const sortedWeeks = Array.from(uniqueWeeks).sort((a, b) =>
                weekOrder.indexOf(a) - weekOrder.indexOf(b)
            );

            return { weeks: sortedWeeks };
        } catch (error) {
            console.error('Error getting unique weeks:', error);
            throw error;
        }
    }

    // Create or update stack unit load heights
    static async createStackUnitLoadHeights(data: { palletId: number, palletLpnNumbers: any[] }): Promise<{ palletLpnNumbers: any[] }> {
        const { palletId, palletLpnNumbers } = data;

        return await db.transaction('rw', [db.palletLpnNumbers, db.unitLoadHeights], async () => {
            try {
                const createdOrUpdatedHeights = [];
                const timestamp = new Date().toISOString();

                for (const palletLpnNumber of palletLpnNumbers) {
                    const { id, lpnNo, endStatusPallet, unitLoadHeights, ...palletLpnNumberData } = palletLpnNumber;

                    // Create or update PalletLpnNumber
                    let palletLpnNumberRecord;
                    if (id) {
                        await db.palletLpnNumbers.update(id, {
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(id);
                    } else {
                        const newId = await db.palletLpnNumbers.add({
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            createdAt: timestamp,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(newId);
                    }

                    if (!palletLpnNumberRecord) continue;

                    // Create or update unit load heights
                    const heightsArray = [];
                    for (const height of unitLoadHeights) {
                        const { id, ...heightData } = height;

                        if (id) {
                            await db.unitLoadHeights.update(id, {
                                ...heightData,
                                updatedAt: timestamp
                            });
                            const updated = await db.unitLoadHeights.get(id);
                            if (updated) heightsArray.push(updated);
                        } else {
                            const newId = await db.unitLoadHeights.add({
                                ...heightData,
                                palletLpnNumberId: palletLpnNumberRecord.id,
                                createdAt: timestamp,
                                updatedAt: timestamp
                            });
                            const created = await db.unitLoadHeights.get(newId);
                            if (created) heightsArray.push(created);
                        }
                    }

                    createdOrUpdatedHeights.push({
                        ...palletLpnNumberRecord,
                        unitLoadHeights: heightsArray
                    });
                }

                return {
                    palletLpnNumbers: createdOrUpdatedHeights
                };
            } catch (error) {
                console.error('Error creating/updating stack unit load heights:', error);
                throw error;
            }
        });
    }
}
